import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  ConfirmDeleteModal,
  Button,
  SideDrawer,
  SkeletonBox,
  SearchInput,
} from '@/components';
import { V2Table } from '@/common/components';
import { PlusIcon } from '../../components/Icons';
import {
  getCRMAttachments,
  createCRMAttachment,
  deleteCRMAttachment,
  updateCRMAttachment,
  getCRMAttachmentDetail,
} from '../api';
import { parseApiError } from '@/common/utils';
import { pageWindow } from '../utils/pagedList';
import { uploadAttachmentToDrive, formatDriveUploadError } from '../../api';
import { useGoogleWorkspaceConnected } from '../../hooks/useGoogleWorkspaceConnected';

import { useFormatters } from '../../hooks/useFormatters';

import { AttachmentLinkCell } from './attachments/AttachmentLinkCell';
import { AttachmentActionMenu } from './attachments/AttachmentActionMenu';
import { MediaViewerModal } from '@/common/components/MediaViewer/MediaViewerModal';
import { env } from '@/common/config';
import { AttachmentTypeIcon } from './attachments/FileTypeIcon';

import { useAttachmentDownload } from '../hooks/useAttachmentDownload';

const PAGE_SIZE = 10;

export function CRMAttachmentsSection({
  companyId,
  contactId,
  readOnly = false,
  canCreate = true,
  canUpdate = true,
  canDelete = true,
}) {
  const queryClient = useQueryClient();
  const googleDrive = useGoogleWorkspaceConnected(companyId);
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [attachmentToDelete, setAttachmentToDelete] = useState(null);
  const [editingAttachmentId, setEditingAttachmentId] = useState(null);
  const [editTitle, setEditTitle] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadDescription, setUploadDescription] = useState('');
  const [previewAttachmentId, setPreviewAttachmentId] = useState(null);
  const [mediaLink, setMediaLink] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);

  const { downloadAttachment, downloadingIds } = useAttachmentDownload();
  const { formatDate } = useFormatters();

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const {
    data: attachmentsData,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: [
      'crm-contact-attachments',
      companyId,
      contactId,
      page,
      debouncedSearch,
    ],
    queryFn: () =>
      getCRMAttachments({
        companyId,
        contactId,
        params: {
          page,
          page_size: PAGE_SIZE,
          ...(debouncedSearch ? { search: debouncedSearch } : {}),
        },
      }),
    enabled: !!companyId && !!contactId,
  });

  const { rows: attachments, totalCount } = pageWindow(
    attachmentsData,
    page,
    PAGE_SIZE,
  );

  const getUploaderName = (attachment) => {
    const uploader = attachment.uploaded_by;
    if (uploader && typeof uploader === 'object') return uploader.name;
    return uploader || 'User';
  };

  const { data: attachmentDetail, isLoading: isLoadingDetail } = useQuery({
    queryKey: [
      'crm-contact-attachment-detail',
      companyId,
      contactId,
      previewAttachmentId,
    ],
    queryFn: () =>
      getCRMAttachmentDetail({
        companyId,
        contactId,
        attachmentId: previewAttachmentId,
      }),
    enabled: !!companyId && !!contactId && !!previewAttachmentId,
  });

  const { mutate: handleAddAttachment, isPending: isAdding } = useMutation({
    mutationFn: (vars) => {
      if (!canCreate) throw new Error('Permission denied.');
      return createCRMAttachment(vars);
    },
    onSuccess: () => {
      toast.success('Attachment uploaded successfully!');
      queryClient.invalidateQueries({
        queryKey: ['crm-contact-attachments', companyId, contactId],
      });
      setPage(1);
      setSelectedFiles([]);
      setUploadTitle('');
      setUploadDescription('');
      setMediaLink('');
      setIsUploadModalOpen(false);
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to upload attachment.');
    },
  });

  const { mutate: handleDeleteAttachment, isPending: isDeleting } = useMutation(
    {
      mutationFn: (vars) => {
        if (!canDelete) throw new Error('Permission denied.');
        return deleteCRMAttachment(vars);
      },
      onSuccess: () => {
        toast.success('Attachment deleted successfully!');
        if (previewAttachmentId === attachmentToDelete) {
          setPreviewAttachmentId(null);
        }
        setAttachmentToDelete(null);
        queryClient.invalidateQueries({
          queryKey: ['crm-contact-attachments', companyId, contactId],
        });
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to delete attachment.');
      },
    },
  );

  const { mutate: handleUpdateAttachment, isPending: isUpdating } = useMutation(
    {
      mutationFn: (vars) => {
        if (!canUpdate) throw new Error('Permission denied.');
        return updateCRMAttachment(vars);
      },
      onSuccess: () => {
        toast.success('Attachment updated successfully!');
        setEditingAttachmentId(null);
        setEditDescription('');
        queryClient.invalidateQueries({
          queryKey: ['crm-contact-attachments', companyId, contactId],
        });
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to update attachment.');
      },
    },
  );

  const [uploadingToDriveIds, setUploadingToDriveIds] = useState(new Set());
  const { mutate: handleUploadToDrive } = useMutation({
    mutationFn: ({ attachmentId }) =>
      uploadAttachmentToDrive({ companyId, attachmentId }),
    onMutate: ({ attachmentId }) =>
      setUploadingToDriveIds((prev) => new Set(prev).add(attachmentId)),
    onSuccess: (data, { attachmentId }) => {
      setUploadingToDriveIds((prev) => {
        const next = new Set(prev);
        next.delete(attachmentId);
        return next;
      });
      if (data?.webViewLink) {
        toast.success(
          <span>
            Uploaded to Drive!{' '}
            <a
              href={data.webViewLink}
              target="_blank"
              rel="noopener noreferrer"
              className="underline font-semibold"
            >
              View in Drive
            </a>
          </span>,
          { duration: 6000 },
        );
      } else {
        toast.success('Attachment uploaded to Google Drive successfully.');
      }
    },
    onError: (err, { attachmentId }) => {
      setUploadingToDriveIds((prev) => {
        const next = new Set(prev);
        next.delete(attachmentId);
        return next;
      });
      toast.error(
        formatDriveUploadError(err, 'Failed to upload attachment to Drive.'),
      );
    },
  });

  const startEditing = (attachment) => {
    setEditingAttachmentId(attachment.id);
    setEditTitle(attachment.title || attachment.file_name || '');
    setEditDescription(attachment.description || '');
  };

  const cancelEditing = () => {
    setEditingAttachmentId(null);
    setEditTitle('');
    setEditDescription('');
  };

  const saveEdit = (attachmentId) => {
    handleUpdateAttachment({
      companyId,
      contactId,
      attachmentId,
      payload: { title: editTitle, description: editDescription },
    });
  };

  function cancelUpload() {
    setSelectedFiles([]);
    setUploadTitle('');
    setUploadDescription('');
    setMediaLink('');
    setIsUploadModalOpen(false);
  }

  const submitUpload = () => {
    const formData = new FormData();

    if (selectedFiles.length === 0 && mediaLink.trim()) {
      // Backend now explicitly accepts links[] as JSON objects
      const linkPayload = {
        url: mediaLink,
        title:
          uploadTitle.trim() || mediaLink.split('/').pop() || 'External Link',
        description: uploadDescription.trim() || '',
      };
      formData.append('links[]', JSON.stringify(linkPayload));
    } else {
      selectedFiles.forEach((file) => {
        formData.append('files[]', file);
      });

      // If a title is provided, append it for each file to match indices
      if (uploadTitle.trim()) {
        selectedFiles.forEach(() => {
          formData.append('titles[]', uploadTitle.trim());
        });
      }

      // If a description is provided, append it for each file to match indices
      if (uploadDescription.trim()) {
        selectedFiles.forEach(() => {
          formData.append('descriptions[]', uploadDescription.trim());
        });
      }
    }

    handleAddAttachment({ companyId, contactId, payload: formData });
  };

  const onFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;

    // Validate file types
    const allowedMimes = [
      'application/pdf',
      'image/jpeg',
      'image/png',
      'application/msword',
      'text/csv',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/plain',
      'image/gif',
      'image/webp',
      'image/svg+xml',
      'image/bmp',
    ];
    const invalidFiles = files.filter((f) => !allowedMimes.includes(f.type));

    if (invalidFiles.length > 0) {
      toast.error('Invalid file type.');
      e.target.value = ''; // Reset input
      return;
    }

    const MAX_SIZE = 50 * 1024 * 1024; // 50MB
    const oversizedFiles = files.filter((f) => f.size > MAX_SIZE);

    if (oversizedFiles.length > 0) {
      toast.error(
        'File size exceeds 50MB limit. Please provide a link instead.',
      );
      e.target.value = ''; // Reset input
      return;
    }

    setSelectedFiles(files);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown size';
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getFileTypeLabel = (fileName) => {
    if (!fileName) return 'File';
    const ext = fileName.split('.').pop().toLowerCase();
    switch (ext) {
      case 'pdf':
        return 'PDF';
      case 'doc':
      case 'docx':
        return 'Document';
      case 'xls':
      case 'xlsx':
      case 'csv':
        return 'Spreadsheet';
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'webp':
      case 'svg':
        return 'Image';
      case 'mp4':
      case 'webm':
      case 'ogg':
        return 'Video';
      case 'mp3':
      case 'wav':
        return 'Audio';
      case 'zip':
      case 'rar':
        return 'Archive';
      default:
        return ext.toUpperCase() + ' File';
    }
  };

  const formatUpdateDate = (dateString) => {
    return formatDate(dateString, {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    });
  };

  const columns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 150,
      minWidth: 110,
      cell: (attachment) => (
        <span className="truncate block text-sm">
          {getUploaderName(attachment)}
        </span>
      ),
    },
    {
      id: 'image',
      label: 'Image',
      defaultWidth: 88,
      minWidth: 80,
      align: 'center',
      skeleton: <SkeletonBox className="w-10 h-10" rounded="rounded-lg" />,
      cell: (attachment) =>
        attachment.attachment_type === 'link' ||
        attachment.attachment_type === 'external_link' ? (
          <AttachmentLinkCell url={attachment.file_url} />
        ) : (
          <AttachmentTypeIcon
            mimeType={attachment.mime_type}
            fileName={attachment.file_name}
          />
        ),
    },
    {
      id: 'title',
      label: 'Title & Type',
      defaultWidth: 180,
      minWidth: 140,
      stopPropagation: true,
      cell: (attachment) => {
        const isEditing = editingAttachmentId === attachment.id;
        const isLink = attachment.attachment_type === 'link';
        if (!isEditing) {
          const title = isLink
            ? 'Link'
            : attachment.title || attachment.file_name || 'File';
          const type = isLink
            ? 'External Link'
            : getFileTypeLabel(attachment.file_name);
          return (
            <div className="flex min-w-0 flex-col">
              <span className="truncate text-sm font-semibold" title={title}>
                {title}
              </span>
              <span className="truncate text-xs text-[#868686]" title={type}>
                {type}
              </span>
            </div>
          );
        }
        return (
          <div className="flex flex-col gap-2">
            <input
              type="text"
              value={editTitle}
              onChange={(e) => setEditTitle(e.target.value)}
              placeholder="Add a title..."
              className="w-full px-3 py-1.5 rounded-[8px] bg-[#F5F5F5] text-sm text-[#1F2241] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
              autoFocus
            />
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={cancelEditing}
                disabled={isUpdating}
                className="h-8 px-3 text-xs"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={() => saveEdit(attachment.id)}
                disabled={isUpdating}
                className="h-8 px-3 text-xs"
              >
                {isUpdating ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        );
      },
    },
    {
      id: 'description',
      label: 'Description',
      defaultWidth: 240,
      minWidth: 160,
      stopPropagation: true,
      cell: (attachment) =>
        editingAttachmentId === attachment.id ? (
          <input
            type="text"
            value={editDescription}
            onChange={(e) => setEditDescription(e.target.value)}
            placeholder="Add a description..."
            className="w-full px-3 py-1.5 rounded-[8px] bg-[#F5F5F5] text-sm text-[#1F2241] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
          />
        ) : (
          <span
            className="line-clamp-2 break-words text-sm"
            title={attachment.description || ''}
          >
            {attachment.description || '-'}
          </span>
        ),
    },
    {
      id: 'updated',
      label: 'Update Date',
      defaultWidth: 170,
      minWidth: 140,
      cell: (attachment) => (
        <span className="truncate block text-sm">
          {formatUpdateDate(attachment.updated_at || attachment.created_at)}
        </span>
      ),
    },
    {
      id: 'actions',
      label: 'Action',
      defaultWidth: 140,
      minWidth: 110,
      align: 'center',
      stopPropagation: true,
      cell: (attachment) => (
        <AttachmentActionMenu
          attachment={attachment}
          isEditing={editingAttachmentId === attachment.id}
          canUpdate={canUpdate}
          canDelete={canDelete}
          readOnly={readOnly}
          onEdit={startEditing}
          onDelete={setAttachmentToDelete}
          onDownload={downloadAttachment}
          isDownloading={downloadingIds.has(attachment.id)}
          onUploadToDrive={
            googleDrive
              ? (att) => handleUploadToDrive({ attachmentId: att.id })
              : undefined
          }
          isUploadingToDrive={uploadingToDriveIds.has(attachment.id)}
        />
      ),
    },
  ];

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col gap-3 overflow-hidden">
      <div className="flex w-full shrink-0 flex-wrap items-center gap-3">
        <div className="order-2 sm:order-1 flex-1 min-w-0">
          <SearchInput
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by title, file, or description"
            className="w-full sm:max-w-md"
            inputClassName="h-[44px]"
          />
        </div>

        {!readOnly && canCreate && (
          <Button
            variant="primary"
            onClick={() => setIsUploadModalOpen(true)}
            disabled={isAdding}
            className="order-1 sm:order-2 h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
            icon={<PlusIcon className="w-4 h-4 sm:w-5 sm:h-5" />}
          >
            <span className="whitespace-nowrap">Upload File</span>
          </Button>
        )}
      </div>

      <SideDrawer
        isOpen={isUploadModalOpen}
        onClose={cancelUpload}
        width="sm"
        className="lead-upload-drawer"
        header={
          <div className="flex items-center justify-between w-full">
            <h2 className="text-xl font-bold text-[#1F2241]">Upload File</h2>
            <button
              type="button"
              onClick={cancelUpload}
              className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.75"
                viewBox="0 0 24 24"
                aria-hidden="true"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>
        }
        bodyClassName="p-6"
        footer={
          <div className="flex justify-end">
            <Button
              variant="primary"
              disabled={
                (selectedFiles.length === 0 && !mediaLink.trim()) || isAdding
              }
              onClick={submitUpload}
              className="h-[44px] px-5"
            >
              <span className="whitespace-nowrap">
                {isAdding ? 'Uploading...' : 'Submit'}
              </span>
              <span aria-hidden="true">→</span>
            </Button>
          </div>
        }
      >
        <div className="bg-[#F5F5F5] rounded-[16px] p-4 sm:p-5 flex flex-col gap-4">
          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Title
            </label>
            <input
              type="text"
              value={uploadTitle}
              onChange={(e) => setUploadTitle(e.target.value)}
              placeholder="Attached File name"
              className="w-full bg-white border border-[#50AB3D] rounded-[10px] px-4 py-2.5 text-sm text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Description
            </label>
            <textarea
              value={uploadDescription}
              onChange={(e) => setUploadDescription(e.target.value)}
              placeholder="Enter text here..."
              rows={3}
              className="w-full bg-white border-0 rounded-[10px] px-4 py-2.5 text-sm text-[#1F2241] placeholder:text-[#868686] resize-none focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Upload File
            </label>
            <div className="relative border-2 border-dashed border-[#EBF4EA] rounded-[12px] p-6 flex flex-col items-center justify-center hover:bg-[#F5F5F5] transition-colors bg-white">
              <input
                type="file"
                className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                multiple
                accept="application/pdf, image/jpeg, image/png, application/msword, text/csv, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/plain, image/gif, image/webp, image/svg+xml, image/bmp"
                onChange={onFileChange}
              />
              <div className="flex flex-col items-center gap-2 text-center pointer-events-none">
                <svg
                  className="w-6 h-6 text-[#50AB3D]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12"
                  />
                </svg>
                {selectedFiles.length > 0 ? (
                  <div className="flex flex-col gap-1 w-full max-w-[240px]">
                    <span className="text-sm font-medium text-[#1F2241]">
                      {selectedFiles.length} file(s) selected
                    </span>
                    <div className="flex flex-col gap-1 mt-2 text-left">
                      {selectedFiles.map((f) => (
                        <div
                          key={f.name}
                          className="flex justify-between items-center text-xs text-[#868686]"
                        >
                          <span className="truncate mr-2 flex-1">{f.name}</span>
                          <span className="shrink-0">
                            {formatFileSize(f.size)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1 items-center">
                    <span className="text-sm text-[#868686]">
                      <span className="text-[#50AB3D] font-semibold">
                        Click To Upload
                      </span>{' '}
                      or drag and drop
                    </span>
                    <span className="text-xs text-[#868686] text-center px-4">
                      Allow : PDF, Image, Docs, Spreadsheets, Media, ZIP (Max :
                      50MB)
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex-1 h-px bg-[#EBF4EA]" />
            <span className="text-xs font-semibold text-[#868686]">OR</span>
            <div className="flex-1 h-px bg-[#EBF4EA]" />
          </div>

          <div>
            <label className="block text-sm font-medium text-text-secondary mb-2">
              Media Link
            </label>
            <input
              type="url"
              value={mediaLink}
              onChange={(e) => setMediaLink(e.target.value)}
              placeholder="https://example.com/your-media-link"
              className="w-full bg-white border-0 rounded-[10px] px-4 py-3 text-sm text-[#1F2241] placeholder:text-[#868686] focus:outline-none focus:ring-1 focus:ring-[#50AB3D]/40"
            />
          </div>
        </div>
      </SideDrawer>

      <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-hidden">
        <V2Table
          className="attachments-table"
          tight
          columns={columns}
          data={isError && !attachmentsData ? [] : attachments}
          rowKey="id"
          storageKey="crm_contact_attachments_column_widths"
          loading={isLoading || (!attachmentsData && !isError)}
          loadingRowCount={PAGE_SIZE}
          notice={
            isError && !attachmentsData
              ? parseApiError(error)?.message || 'Failed to load attachments'
              : null
          }
          empty="No attachments found."
          onRowClick={(attachment) => {
            if (
              attachment.attachment_type === 'link' ||
              attachment.attachment_type === 'external_link'
            )
              return;
            setPreviewAttachmentId(attachment.id);
          }}
          pagination={{
            currentPage: page,
            totalCount,
            itemsPerPage: PAGE_SIZE,
            onChange: setPage,
          }}
        />
      </div>

      <ConfirmDeleteModal
        isOpen={!!attachmentToDelete}
        title="Delete Attachment"
        description="Are you sure you want to delete this attachment? This action cannot be undone."
        onCancel={() => setAttachmentToDelete(null)}
        onConfirm={() =>
          handleDeleteAttachment({
            companyId,
            contactId,
            attachmentId: attachmentToDelete,
          })
        }
        isDeleting={isDeleting}
      />

      <MediaViewerModal
        isOpen={!!previewAttachmentId && !isLoadingDetail && !!attachmentDetail}
        onClose={() => setPreviewAttachmentId(null)}
        mediaList={
          attachmentDetail
            ? [
                {
                  id: attachmentDetail.id,
                  mediaUrl: attachmentDetail.file_url?.startsWith('http')
                    ? attachmentDetail.file_url
                    : `${env.photoBaseUrl}${attachmentDetail.file_url}`,
                  fileName: attachmentDetail.file_name,
                  mimeType:
                    attachmentDetail.mime_type ||
                    attachmentDetail.file_name?.split('.').pop(),
                  time: new Date(attachmentDetail.created_at).toLocaleString(),
                },
              ]
            : []
        }
        initialIndex={0}
        contactName={attachmentDetail?.file_name}
      />
    </div>
  );
}
